import { FootballBreadcrumbsComponent } from '@/app/components/shared/football-breadcrumbs/football-breadcrumbs.component';
import { NoResultComponent } from '@/app/components/shared/no-result/no-result.component';
import { RoutingService } from '@/app/services/routing.service';
import { SeoService } from '@/app/services/seo';
import { TranslationService } from '@/app/services/translation.service';
import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CompetitionMatchSummary } from '../match-details/models';
import {
  CompetitionDetailsCardComponent,
  CompetitionDetailsSkeletonComponent,
  CompetitionTabsComponent,
} from './components';
import { ECompetitionTab, ICompetitionSummary } from './models';
import { CompetitionDetailsService } from './services';
import { MatchesComponent, ResultsComponent, StandingsComponent } from './tabs';

@Component({
  selector: 'app-competition-details',
  standalone: true,
  imports: [
    MatchesComponent,
    ResultsComponent,
    StandingsComponent,
    NoResultComponent,
    CompetitionTabsComponent,
    CompetitionDetailsSkeletonComponent,
    CompetitionDetailsCardComponent,
    FootballBreadcrumbsComponent,
  ],
  templateUrl: './competition-details.component.html',
})
export class FootballCompetitionDetailsComponent {
  private route = inject(ActivatedRoute);
  private routingService = inject(RoutingService);
  private router = inject(Router);
  private translationService = inject(TranslationService);
  private competitionDetailsService = inject(CompetitionDetailsService);
  private seoService = inject(SeoService);

  competitionId = signal<string>('');
  slug = signal<string | null>(null);
  selectedTab = signal<ECompetitionTab>(ECompetitionTab.MATCHES);

  fetchStatusComputed = computed(() => ({
    isLoading: this.competitionDetailsService.isLoadingCompetitionDetails(),
    isError: this.competitionDetailsService.isErrorCompetitionDetails(),
  }));

  competition = computed(() =>
    this.competitionDetailsService.competitionDetails()
  );

  constructor() {
    window.scrollTo(0, 0);

    this.route.paramMap.subscribe((params) => {
      const id = params.get('competitionId');
      const slug = params.get('slug');

      if (id) {
        this.competitionId.set(id);
        this.slug.set(slug);
        this.competitionDetailsService.getCompetitionDetails(id, () => {
          if (slug) {
            this.validateSlug();
          }

          // insert SEO
          const name = this.translationService.getTraduccionName(
            this.competition()?.NameTranslation as any,
            this.competition()?.Name || 'NA'
          );

          this.seoService.setLeagueSeo({
            name,
            url: window.location.href,
            // image: '/assets/match.jpg',
          });
        });
        this.selectedTab.set(ECompetitionTab.MATCHES);
      }
    });
  }

  validateSlug() {
    const { Name, NameTranslation, Id } = this.competition()!;

    // Redirigir a la URL correcta
    this.router.navigate(
      [
        this.routingService.getTraslateUrlCompetitionDetails(
          {
            es: NameTranslation?.['es'],
            en: NameTranslation?.['en'],
          },
          Name,
          Id
        ),
      ],
      {
        replaceUrl: true,
        queryParamsHandling: 'preserve',
      }
    );
  }

  handleSelectTab(tabValue: ECompetitionTab) {
    this.selectedTab.set(tabValue);
  }

  getCompetitionBreadcrumbsData(): CompetitionMatchSummary {
    return {
      Id: this.competition()?.Id || '',
      ImageUrl: this.competition()?.ImageUrl || '',
      Name: this.competition()?.Name || 'NA',
      NameTranslation: this.competition()?.NameTranslation || null,
      TsExternalId: this.competition()?.TsExternalId || '',
      YoutubeUrl: '',
    };
  }

  getCompetitionDetailsData(): ICompetitionSummary {
    return {
      Id: this.competition()?.Id || '',
      TsExternalId: this.competition()?.TsExternalId || '',
      Name: this.competition()?.Name || '',
      CurrentSeason: this.competition()?.CurrentSeason || '',
      ImageUrl: this.competition()?.ImageUrl || '',
      IsFavorite: this.competition()?.IsFavorite || false,
      NameTranslation: this.competition()?.NameTranslation || null,
    };
  }
}
